/**
 * Prompts Management Page with Visual Flow Builder
 * Create prompts visually by connecting instructions, tools, and resources
 */

import React, { useState, useEffect } from 'react';
import { FlowPromptBuilder } from '../components/prompts';
import { MessageSquare, Plus, Edit, Trash2, Play, Sparkles, X } from 'lucide-react';
import api from '../services/api';

interface Prompt {
  id: string;
  name: string;
  description: string | null;
  template: string;
  flow_data?: any;
  tags: string[];
  use_count: number;
  created_at: string;
}

export const PromptsManagement: React.FC = () => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveData, setSaveData] = useState<{ prompt: string; flowData: any } | null>(null);

  // Form state for saving
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tags: '',
  });

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    try {
      setLoading(true);
      const response = await api.prompts.list();
      setPrompts(response.prompts || []);
    } catch (error) {
      console.error('Error loading prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingPrompt(null);
    setShowBuilder(true);
  };

  const handleEdit = (prompt: Prompt) => {
    setEditingPrompt(prompt);
    setFormData({
      name: prompt.name,
      description: prompt.description || '',
      tags: prompt.tags?.join(', ') || '',
    });
    setShowBuilder(true);
  };

  const handleSave = (optimizedPrompt: string, flowData: any) => {
    setSaveData({ prompt: optimizedPrompt, flowData });
    setShowSaveModal(true);
  };

  const handleConfirmSave = async () => {
    if (!saveData) return;

    try {
      const tags = formData.tags.split(',').map(t => t.trim()).filter(Boolean);

      if (editingPrompt) {
        // Update existing
        await api.prompts.update(editingPrompt.id, {
          name: formData.name,
          description: formData.description || undefined,
          template: saveData.prompt,
          tags,
          flow_data: saveData.flowData,
        });
      } else {
        // Create new
        await api.prompts.create({
          name: formData.name,
          description: formData.description || undefined,
          template: saveData.prompt,
          tags,
          flow_data: saveData.flowData,
        });
      }

      setShowSaveModal(false);
      setShowBuilder(false);
      setSaveData(null);
      setFormData({ name: '', description: '', tags: '' });
      loadPrompts();
    } catch (error) {
      console.error('Error saving prompt:', error);
      alert('Error al guardar el prompt');
    }
  };

  const handleDelete = async (promptId: string) => {
    if (!confirm('¿Estás seguro de eliminar este prompt?')) return;

    try {
      await api.prompts.delete(promptId);
      loadPrompts();
    } catch (error) {
      console.error('Error deleting prompt:', error);
      alert('Error al eliminar el prompt');
    }
  };

  if (showBuilder) {
    return (
      <div className="h-screen flex flex-col">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {editingPrompt ? 'Editar Prompt' : 'Crear Nuevo Prompt'}
              </h1>
              <p className="text-gray-600 mt-1">
                Construye tu prompt visualmente conectando instrucciones, tools y recursos
              </p>
            </div>
            <button
              onClick={() => {
                setShowBuilder(false);
                setEditingPrompt(null);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <X size={20} />
              Cancelar
            </button>
          </div>
        </div>

        {/* Flow Builder */}
        <div className="flex-1">
          <FlowPromptBuilder
            onSave={handleSave}
            initialFlow={editingPrompt?.flow_data}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <MessageSquare className="text-blue-600" size={32} />
            Gestión de Prompts
          </h1>
          <p className="text-gray-600 mt-1">
            Crea prompts optimizados con el editor visual de flujo
          </p>
        </div>
        <button
          onClick={handleCreateNew}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={20} />
          Crear Prompt Visual
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Prompts</p>
              <p className="text-2xl font-bold text-gray-900">{prompts.length}</p>
            </div>
            <MessageSquare className="text-blue-500" size={32} />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Prompts con IA</p>
              <p className="text-2xl font-bold text-gray-900">
                {prompts.filter(p => p.flow_data).length}
              </p>
            </div>
            <Sparkles className="text-purple-500" size={32} />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Usos</p>
              <p className="text-2xl font-bold text-gray-900">
                {prompts.reduce((sum, p) => sum + (p.use_count || 0), 0)}
              </p>
            </div>
            <Play className="text-green-500" size={32} />
          </div>
        </div>
      </div>

      {/* Prompts Grid */}
      {loading ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Cargando prompts...</p>
        </div>
      ) : prompts.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <MessageSquare className="mx-auto text-gray-400" size={48} />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No hay prompts</h3>
          <p className="mt-2 text-gray-600">
            Crea tu primer prompt con el editor visual
          </p>
          <button
            onClick={handleCreateNew}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={20} />
            Crear Primer Prompt
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {prompts.map((prompt) => (
            <div
              key={prompt.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold text-gray-900 truncate">{prompt.name}</h3>
                    {prompt.flow_data && (
                      <Sparkles size={16} className="text-purple-600 flex-shrink-0" />
                    )}
                  </div>
                  {prompt.description && (
                    <p className="text-sm text-gray-600 line-clamp-2">{prompt.description}</p>
                  )}
                </div>
              </div>

              {/* Tags */}
              {prompt.tags && prompt.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {prompt.tags.slice(0, 3).map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded"
                    >
                      {tag}
                    </span>
                  ))}
                  {prompt.tags.length > 3 && (
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                      +{prompt.tags.length - 3}
                    </span>
                  )}
                </div>
              )}

              {/* Stats */}
              <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                <span>Usado {prompt.use_count || 0} veces</span>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(prompt)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm"
                >
                  <Edit size={16} />
                  Editar
                </button>
                <button
                  onClick={() => handleDelete(prompt.id)}
                  className="flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-sm"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Save Modal */}
      {showSaveModal && saveData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Guardar Prompt</h2>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre del Prompt *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ej: Análisis de datos con contexto"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descripción
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    placeholder="Descripción del prompt..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags (separados por comas)
                  </label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="análisis, datos, reporte"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prompt Optimizado
                  </label>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                      {saveData.prompt}
                    </pre>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowSaveModal(false);
                    setSaveData(null);
                  }}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmSave}
                  disabled={!formData.name.trim()}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 transition-colors"
                >
                  Guardar Prompt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
